import { useState, type InputHTMLAttributes } from 'react';
import { sanitizeMoneyInput, normalizeMoney, formatAmountDisplay } from '../utils/money';
import './MoneyInput.css';

// Shared currency input. Displays a fixed '$' prefix and, on blur, formats the
// value to 2 decimals with thousands separators ('6' → '$6.00', '1234' →
// '$1,234.00'). While focused it shows the plain editable number, so the cursor
// never jumps. The value handed back via onChange is always a clean numeric
// STRING (no '$', no commas) — same contract as the raw inputs it replaces, so
// callers' submit/validation logic is unchanged. Mirrors <PhoneInput>'s
// value/onChange string contract + <DurationStepper>'s focus-buffer/blur-commit.

type Inherited = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type' | 'inputMode'
>;

export interface MoneyInputProps extends Inherited {
  /** Canonical 2-dp numeric string, e.g. "1234.00" (or "" when blank). */
  value: string;
  /** Called with the cleaned numeric string (no symbol/commas). */
  onChange: (next: string) => void;
  /** Permit a leading '-' (e.g. invoice discount lines). Default false. */
  allowNegative?: boolean;
}

export function MoneyInput({
  value,
  onChange,
  allowNegative = false,
  className,
  onFocus,
  onBlur,
  ...rest
}: MoneyInputProps): JSX.Element {
  const [focused, setFocused] = useState(false);
  const [buf, setBuf] = useState('');

  // Focused → what the user is typing; blurred → grouped $X.XX display.
  const display = focused ? buf : formatAmountDisplay(value);

  return (
    <span className="money-input-wrap">
      <span className="money-input-prefix" aria-hidden="true">$</span>
      <input
        {...rest}
        type="text"
        inputMode="decimal"
        className={`form-input money-input ${className ?? ''}`}
        value={display}
        onFocus={(e) => {
          setBuf(value); // reveal the raw editable number
          setFocused(true);
          onFocus?.(e);
        }}
        onChange={(e) => {
          const s = sanitizeMoneyInput(e.target.value, allowNegative);
          setBuf(s);
          onChange(s);
        }}
        onBlur={(e) => {
          const n = normalizeMoney(buf);
          setBuf(n);
          setFocused(false);
          if (n !== value) onChange(n);
          onBlur?.(e);
        }}
      />
    </span>
  );
}
